import React from 'react'
import { FormTextarea, FormCheckbox } from 'protocol-setup/components/FormComponents';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { postWithAuth } from 'protocol-setup/redux/makeFetch';
import { defaultErrorMessage, regulatorySignOffAdditionalSignOff, regulatorySignoffReminderUrl } from 'protocol-setup/redux/constants';
import { OpenErrorNotification, OpenSuccessNotification } from 'protocol-setup/components/notification';
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ButtonGroupSignoff from 'protocol-setup/components/FormComponents/ButtonGroupSignoff';
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import text from "protocol-setup/constants/text";
import errorMessages, { COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";

export const schema = yup.object().shape({
  irocAdminSignoff: yup.boolean().oneOf([true], errorMessages.regulatory.signOff.acknowledgmentRequired),
  irocAdminComments: yup.string().max(COMMENTS_MAX_LENGTH, errorMessages.regulatory.signOff.additional.irocAdminComments.max).optional()
});

export const handleSubmitForm = async (data, requestNumber, psrrsoId, onSuccessCallback) => {
  const { irocAdminSignoff, irocAdminComments } = data;
  try {
    const payload = {
      psrrsoId,
      requestNumber,
      irocAdminSignoff: Boolean(irocAdminSignoff),
      irocAdminComments
    }
    await postWithAuth(regulatorySignOffAdditionalSignOff, payload);
    if (onSuccessCallback) {
      await onSuccessCallback();
    }
    OpenSuccessNotification({
      message: "Record successfully saved.",
      autoHide: true
    });
    return true;
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
    return false;
  }
};

export const onSendReminder = async (requestNumber) => {
  try {
    await postWithAuth(regulatorySignoffReminderUrl, {
      notificationId: 'RSubIROC',
      requestNumber
    });
    OpenSuccessNotification({
      message: text.regulatory.signOff.additional.reminderSuccess,
      autoHide: true
    });
  } catch (error) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
  }
}
const RegulatoryAdditionalSignOff = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, regSignoff } = protocolSetupData;

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      irocAdminSignoff: regSignoff?.irocAdminSignoff || '',
      irocAdminComments: regSignoff?.irocAdminComments || ''
    }
  });

  const hasSignOffPermission = useSetupPermission(ProtectionElements.REGULATORY_SIGNOFF_ADDITIONAL);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, regSignoff?.psrrsoId, onSuccessCallback),
    dirtyFields,
    "Signoff: Additional Signoff",
    "Signoff"
  );

  const onReset = () => {
    reset();
  };

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${readOnly ? "read-only" : ""
        }`}
    >      <form
      id="RegAdditionalSignOff"
      className="bg-transparent mt-3"
      onSubmit={handleSubmit((data) => {
        const d = { ...data, requestNumber }
        onSubmitWithHistory(d, onSuccess)
      }
      )}
    >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul>
            <ul className="col-8 mr-4 mb-0 form-sub-group" role="group">
              <li className="d-block">
                <div className="content-group-title">{text.regulatory.signOff.additional.sectionTitle}</div>
                <div className="d-block">
                  <FormCheckbox
                    className="pl-0 mb-3 d-flex align-items-start"
                    labelClassName="color-dark-blue align-left col-12"
                    name="irocAdminSignoff"
                    section="regulatory"
                    form="regulatoryAdditionalSignOff"
                    required
                    control={control}
                    disabled={!isSignOffEditable}
                    readOnly={!isSignOffEditable}
                    wrapperClassName={!isSignOffEditable ? 'read-only-show' : ''}
                    trigger={trigger}
                  />
                </div>
                <div className="d-block">
                  <FormTextarea
                    name="irocAdminComments"
                    section="regulatory"
                    form="regulatoryAdditionalSignOff"
                    control={control}
                    disabled={!isSignOffEditable}
                    trigger={trigger}
                  />
                </div>
              </li>
            </ul>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          <ButtonGroupSignoff
            onSubmit={handleSubmitForm}
            onReset={onReset}
            isSubmitting={isSubmitting}
            control={control}
            onSendReminder={() =>
              !readOnly
                ? onSendReminder(requestNumber)
                : undefined}
            protectionElement={ProtectionElements.REGULATORY_SIGNOFF_ADDITIONAL}
            protectionElementSR={ProtectionElements.REGULATORY_SIGNOFF_ADDITIONAL_REMINDER}
          />
        </div>
      </form>
    </div>
  );
};

export default RegulatoryAdditionalSignOff;